import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Card, Typography, Chip, Stack, Divider, Button, Sheet, CircularProgress } from '@mui/joy';
import { emailActivity, type EmailActivityLog } from '../lib/emailActivityService';
import { FaArrowLeft } from 'react-icons/fa';
import { IconWrapper } from '../components/IconWrapper';

type Attachment = {
  name: string;
  contentType?: string;
  size?: number;
};

function getOpenMeta(log: EmailActivityLog): { openedAt?: string; openCount?: number; internalOpenCount?: number } {
  const meta: any = (log as any)?.metadata;
  if (!meta || typeof meta !== 'object') return {};
  const openedAt = typeof meta.opened_at === 'string' ? meta.opened_at : undefined;
  const openCount = typeof meta.open_count === 'number' ? meta.open_count : undefined;
  const internalOpenCount = typeof meta.internal_open_count === 'number' ? meta.internal_open_count : undefined;
  return { openedAt, openCount, internalOpenCount };
}

function getCustomerActionLabel(log: EmailActivityLog): string | null {
  const meta: any = (log as any)?.metadata;
  const action = String(meta?.customerAction?.action || meta?.customer_action?.action || '').trim().toLowerCase();
  if (!action) return null;
  if (action === 'accept' || action === 'accepted') return 'Customer accepted';
  if (action === 'reject' || action === 'rejected') return 'Customer rejected';
  if (action === 'query') return 'Customer query';
  if (action === 'opened' || action === 'open') return 'Customer opened';
  return 'Customer action';
}

function getCustomerActionDetails(log: EmailActivityLog): { action?: string; reason?: string; message?: string; at?: string } {
  const meta: any = (log as any)?.metadata;
  const ca = meta?.customerAction || meta?.customer_action || null;
  if (!ca || typeof ca !== 'object') return {};
  const action = typeof ca.action === 'string' ? ca.action : undefined;
  const reason = typeof ca.reason === 'string' ? ca.reason : undefined;
  const message = typeof ca.message === 'string' ? ca.message : undefined;
  const at = typeof ca.at === 'string' ? ca.at : undefined;
  return { action, reason, message, at };
}

const EmailActivityDetailPage: React.FC = () => {
  const { emailId: id } = useParams<{ emailId: string }>();
  const navigate = useNavigate();
  const [log, setLog] = useState<EmailActivityLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLog = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!id) throw new Error('No email activity ID provided');
        const { data, error } = await emailActivity.getById(id);
        if (error) throw new Error(error);
        setLog(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load email activity');
      } finally {
        setLoading(false);
      }
    };
    fetchLog();
  }, [id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'success';
      case 'failed': return 'danger';
      case 'draft': return 'neutral';
      default: return 'neutral';
    }
  };

  const getStatusColorForLog = (log: EmailActivityLog) => {
    const s = String(log.status || '').trim().toLowerCase();
    if (s === 'received') {
      const details = getCustomerActionDetails(log);
      const action = String(details.action || '').trim().toLowerCase();
      if (action === 'reject' || action === 'rejected') return 'danger';
      if (action === 'accept' || action === 'accepted') return 'success';
      if (action === 'query') return 'warning';
      if (action === 'opened' || action === 'open') return 'primary';
      return 'neutral';
    }
    return getStatusColor(s);
  };

  const getStatusLabel = (log: EmailActivityLog) => {
    const s = String(log.status || '').trim().toLowerCase();
    if (s === 'received') return getCustomerActionLabel(log) || 'Customer action';
    return String(log.status || '').trim() || 'unknown';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !log) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="danger" level="h4">{error || 'Activity entry not found'}</Typography>
        <Button
          startDecorator={<IconWrapper icon={FaArrowLeft} />}
          onClick={() => navigate('/admin')}
          sx={{ mt: 2 }}
        >
          Back to Activity Monitor
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, width: '100%', maxWidth: '100vw', boxSizing: 'border-box' }}>
      <Button
        startDecorator={<IconWrapper icon={FaArrowLeft} />}
        variant="plain"
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back
      </Button>
      <Card sx={{ p: 3 }}>
        <Typography level="h3" mb={2}>Activity Detail</Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2}>
          <Box>
            <Typography level="body-sm" color="neutral">Subject:</Typography>
            <Typography>{log.subject}</Typography>
          </Box>
          <Box>
            <Typography level="body-sm" color="neutral">Recipient:</Typography>
            <Typography>{log.recipient}</Typography>
          </Box>
          <Box>
            <Typography level="body-sm" color="neutral">Status:</Typography>
            <Chip variant="soft" color={getStatusColorForLog(log)} size="sm">{getStatusLabel(log)}</Chip>
          </Box>
          {(() => {
            const details = getCustomerActionDetails(log);
            const action = String(details.action || '').trim().toLowerCase();
            if (action !== 'reject' && action !== 'rejected') return null;
            const reason = String(details.reason || '').trim();
            if (!reason) return null;
            return (
              <Box>
                <Typography level="body-sm" color="neutral">Rejection reason:</Typography>
                <Typography>{reason}</Typography>
              </Box>
            );
          })()}
          {(() => {
            const { openedAt, openCount, internalOpenCount } = getOpenMeta(log);
            if (!openedAt && !internalOpenCount) return null;
            return (
              <Box>
                <Typography level="body-sm" color="neutral">Opens:</Typography>
                <Typography>
                  {openedAt ? `Customer opened: ${new Date(openedAt).toLocaleString('en-GB')}` : 'Customer opened: —'}
                  {typeof openCount === 'number' ? ` (count: ${openCount})` : ''}
                  {typeof internalOpenCount === 'number' ? ` · Staff previews: ${internalOpenCount}` : ''}
                </Typography>
              </Box>
            );
          })()}
          {log.error_message && (
            <Box>
              <Typography level="body-sm" color="danger">Error:</Typography>
              <Typography color="danger">{log.error_message}</Typography>
            </Box>
          )}
          <Divider />
          <Box>
            <Typography level="body-sm" color="neutral">Content:</Typography>
            <Sheet
              variant="outlined"
              sx={{ mt: 1, p: 2, borderRadius: 'sm', backgroundColor: 'background.level1' }}
            >
              <div dangerouslySetInnerHTML={{ __html: log.content_html }} />
            </Sheet>
          </Box>
          {log.metadata && typeof log.metadata === 'object' && Array.isArray((log.metadata as any).attachments) && (log.metadata as any).attachments.length > 0 && (
            <Box>
              <Typography level="body-sm" color="neutral">Attachments:</Typography>
              <Stack spacing={1} mt={1}>
                {((log.metadata as any).attachments as Attachment[]).map((attachment, idx) => (
                  <Chip key={idx} variant="outlined">{attachment.name}</Chip>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </Card>
    </Box>
  );
};

export default EmailActivityDetailPage; 